import { sgfToPoint } from './coordinateUtils';
import type { Move, StoneType } from '../types/game';

type Stones = Map<string, StoneType>;

function neighborKeys(key: string, boardSize: number): string[] {
  const [row, col] = key.split(',').map(Number);
  const result: string[] = [];
  if (row > 0)            result.push(`${row - 1},${col}`);
  if (row < boardSize - 1) result.push(`${row + 1},${col}`);
  if (col > 0)            result.push(`${row},${col - 1}`);
  if (col < boardSize - 1) result.push(`${row},${col + 1}`);
  return result;
}

function groupHasLiberty(stones: Stones, startKey: string, boardSize: number): { group: string[]; alive: boolean } {
  const color = stones.get(startKey);
  if (!color) return { group: [], alive: false };

  const group: string[] = [];
  const visited = new Set<string>();
  const stack = [startKey];
  let alive = false;

  while (stack.length) {
    const key = stack.pop()!;
    if (visited.has(key)) continue;
    visited.add(key);
    group.push(key);
    for (const nk of neighborKeys(key, boardSize)) {
      if (!stones.has(nk)) { alive = true; }
      else if (stones.get(nk) === color && !visited.has(nk)) { stack.push(nk); }
    }
  }
  return { group, alive };
}

/** Replay moves up to `upToMove` (1-based) and return the resulting board state.
 *  `handicap >= 2` means white plays first (handicap stones are pre-placed). */
export function computeBoardState(moves: Move[], boardSize: number, upToMove: number, handicap = 0): Stones {
  const stones: Stones = new Map();
  const limit = Math.min(upToMove, moves.length);

  // Handicap ≥ 2: white plays the first actual move after handicap stones are placed
  const colors: [StoneType, StoneType] = handicap >= 2
    ? ['white', 'black']
    : ['black', 'white'];

  for (let i = 0; i < limit; i++) {
    const move = moves[i];
    const pt = sgfToPoint(move.mv, boardSize);
    if (!pt) continue; // pass / resign

    const color: StoneType = colors[i % 2];
    const enemy: StoneType = color === 'black' ? 'white' : 'black';
    const key = `${pt.row},${pt.col}`;
    stones.set(key, color);

    // Capture surrounded enemy groups
    for (const nk of neighborKeys(key, boardSize)) {
      if (stones.get(nk) === enemy) {
        const { group, alive } = groupHasLiberty(stones, nk, boardSize);
        if (!alive) group.forEach(k => stones.delete(k));
      }
    }
  }

  return stones;
}
